import React from "react";
import { useTranslation } from "react-i18next";
import { Download, History, Pencil, Trash2, TriangleAlert } from "lucide-react";
import ListItemCardCst from "@components/ListItemCardCst";
import {
  ListItemAction,
  ListItemBadge,
} from "@components/ListItemCardCst/types";
import { mapEntityToCard } from "../../../Catalog/mapEntityToCard";
import { CatalogEntityType } from "../../../Catalog/types";
import { formatEnrollTypeLabel } from "../../../Catalog/UsersAndGroups/config/enrollTypeMap";

interface UserLoEnrollmentRowProps {
  entity: any;
  entityType: CatalogEntityType;
  onEditEnrollment?: (entity: any) => void;
  onDeleteEnrollment?: (entity: any) => void;
  onEditGroupEnrollment?: (entity: any) => void;
  onDeleteGroupEnrollment?: (entity: any) => void;
  onHistory?: (entity: any) => void;
  onDownloadCertificate?: (entity: any) => void;
}

const UserLoEnrollmentRow: React.FC<UserLoEnrollmentRowProps> = ({
  entity,
  entityType,
  onEditEnrollment,
  onDeleteEnrollment,
  onEditGroupEnrollment,
  onDeleteGroupEnrollment,
  onHistory,
  onDownloadCertificate,
}) => {
  const { t } = useTranslation("catalog-v2");
  const { t: tActivity } = useTranslation("wizard-activity");
  const { t: tEnroll } = useTranslation("users-and-groups");

  const cardProps = mapEntityToCard(entity, entityType, {
    t,
    tActivity,
    onDetails: () => {}, //TODO implementare onDetails (pagina non ancora creata)
  });

  const statusKey =
    entity.status === "R"
      ? "refused"
      : entity.status === "T"
        ? "top-down-needed"
        : entity.completeDate
          ? "completed"
          : (entity.progPerc ?? 0) > 0
            ? "in-progress"
            : "to-start";

  const enrollmentBadges: ListItemBadge[] = [
    ...(entity.enrollType
      ? [{ label: formatEnrollTypeLabel(entity.enrollType) }]
      : []),
    {
      label: String(tEnroll(`card.status.${statusKey}`)),
      variant:
        statusKey === "refused"
          ? "error"
          : statusKey === "top-down-needed"
            ? "warning"
            : statusKey === "completed"
              ? "success"
              : statusKey === "in-progress"
                ? "info"
                : "default",
    },
    ...(entity.mandatory
      ? [
          {
            variant: "warning" as const,
            icon: <TriangleAlert size={14} />,
            label: "",
          },
        ]
      : []),
  ];

  const fromGroup = entity.massiveGroupId != null;

  // Le action di enrollment sono disponibili solo quando il chiamante espone
  // onDeleteEnrollment: stesso pattern usato da UserEnrollmentRow.
  const moreActions: ListItemAction[][] = onDeleteEnrollment
    ? [
        [
          {
            label: String(tEnroll("card.actions.edit-enrollment")),
            icon: <Pencil size={14} />,
            onClick: () => onEditEnrollment?.(entity),
            disabled: !onEditEnrollment,
          },
          {
            label: String(tEnroll("card.actions.delete-enrollment")),
            icon: <Trash2 size={14} />,
            onClick: () => onDeleteEnrollment(entity),
          },
        ],
        ...(fromGroup
          ? [
              [
                {
                  label: String(tEnroll("card.actions.edit-group-enrollment")),
                  icon: <Pencil size={14} />,
                  onClick: () => onEditGroupEnrollment?.(entity),
                  disabled: !onEditGroupEnrollment || !entity.groupType,
                },
                {
                  label: String(
                    tEnroll("card.actions.delete-group-enrollment"),
                  ),
                  icon: <Trash2 size={14} />,
                  onClick: () => onDeleteGroupEnrollment?.(entity),
                  disabled: !onDeleteGroupEnrollment || !entity.groupType,
                },
              ],
            ]
          : []),
        [
          {
            label: String(tEnroll("card.actions.enrollment-history")),
            icon: <History size={14} />,
            onClick: () => onHistory?.(entity),
            disabled: !onHistory,
          },
        ],
        [
          {
            label: String(tEnroll("card.actions.download-certificate")),
            icon: <Download size={14} />,
            onClick: () => onDownloadCertificate?.(entity),
            disabled: !onDownloadCertificate || entity.certificateId == null,
          },
        ],
      ]
    : [];

  return (
    <ListItemCardCst
      {...cardProps}
      actions={[]}
      badges={[...(cardProps.badges || []), ...enrollmentBadges]}
      moreActions={moreActions}
    />
  );
};

export default UserLoEnrollmentRow;
